import { AppConfigurationKey } from '../../../../model/enums/appConfigurationKey'
import { getActiveCompany, getAppConfigurationValue } from '../../../../redux'
import AxiosInstance from '../../../http-config'
import { EnumPriceType } from '../../../oData/enums/enumPriceType'
import { EnumCommodity, EnumPriceComponentReference } from '../../enums'
import {
  ArgentiaPricingTableModel,
  CreatePriceComponentRequest,
  PriceComponentAPI,
  PriceComponentModelV2,
  PriceComponentTypeModel,
  PriceComponentV2RequestModel,
  UpdatePriceComponentValueRequest
} from './model/PriceComponent'

export const apiGetPriceComponents = async ({
  contractId,
  flexibilityId,
  leadId
}: {
  contractId?: number
  flexibilityId?: number
  leadId?: number
}): Promise<PriceComponentAPI[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:59923'

  let query = ''
  if (contractId) {
    query = `contractId=${contractId}`
  } else if (flexibilityId) {
    query = `flexibilityId=${flexibilityId}`
  } else if (leadId) {
    query = `leadId=${leadId}`
  }

  return await AxiosInstance.get(`${apiSuffixUrl}/api/PriceComponents?${query}`).then(
    response => response.data
  )
}

export const apiGetPriceComponent = async ({ id }: { id?: number }): Promise<PriceComponentAPI> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(`${apiSuffixUrl}/api/PriceComponents/${id}`).then(
    response => response.data
  )
}

export const apiGetPriceComponentByPrice = async ({
  priceID
}: {
  priceID?: number
}): Promise<PriceComponentAPI[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/PriceComponents/GetPriceComponentByPrice?priceId=${priceID}`
  ).then(response => response.data)
}

export const apiGetPriceComponentDefaults = async (
  referenceType?: number,
  priceType?: number,
  commodity?: number,
  priceComponentTypeID?: number
): Promise<PriceComponentAPI[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  const params = {
    referenceType,
    priceType,
    commodity,
    priceComponentTypeID
  }

  return await AxiosInstance.get(`${apiSuffixUrl}/api/PriceComponents/GetPriceComponentDefaults`, {
    params
  }).then(response => response.data)
}

// #region  -------------------- PRICE COMPONENT TYPES --------------------
export const apiGetPriceComponentTypeById = async (
  priceComponentTypeID: number
): Promise<PriceComponentTypeModel> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  const params = {
    id: priceComponentTypeID
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/PriceComponents/GetPriceComponentTypeById`, {
    params
  }).then(response => response.data)
}

export const apiGetPriceComponentTypes = async (
  referenceType?: EnumPriceComponentReference,
  priceType?: EnumPriceType,
  commodity?: number,
  expandDummy: boolean = false
): Promise<PriceComponentTypeModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  const params = {
    referenceType,
    priceType,
    commodity,
    expandDummy
  }
  return await AxiosInstance.get(`${apiSuffixUrl}/api/PriceComponents/GetPriceComponentTypes`, {
    params
  }).then(response => response.data)
}

export const apiPostPriceComponentType = async (
  request: PriceComponentTypeModel
): Promise<PriceComponentTypeModel> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:59923'

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/PriceComponents/PriceComponentType`,
    request
  ).then(response => response.data)
}

export const apiPatchPriceComponentType = async (
  priceComponentTypeId: number,
  request: PriceComponentTypeModel
): Promise<PriceComponentTypeModel> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:59923'

  return await AxiosInstance.patch(
    `${apiSuffixUrl}/api/PriceComponents/PriceComponentType(${priceComponentTypeId})`,
    request
  ).then(response => response.data)
}

export const apiDeletePriceComponentType = async (priceComponentTypeId: number): Promise<null> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:59923'
  return await AxiosInstance.delete(
    `${apiSuffixUrl}/api/PriceComponents/PriceComponentType(${priceComponentTypeId})`
  ).then(response => response.data)
}

export const apiGetPriceComponentByPcTypeID = async (
  priceComponentTypeId: number,
  commodity: EnumCommodity,
  companyId: number
): Promise<PriceComponentModelV2[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    commodity,
    companyId
  }
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/PriceComponents/PriceComponentsByPriceComponentTypeId(${priceComponentTypeId})`,
    {
      params
    }
  ).then(response => {
    return response.data
  })
}

export const apiGetDefaultPriceComponentByTypeId = async (
  priceComponentTypeId: number,
  referenceType: EnumPriceComponentReference,
  commodity: EnumCommodity,
  companyId: number
): Promise<PriceComponentModelV2 | null> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const params = {
    commodity,
    companyID: companyId,
    referenceType
  }
  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/PriceComponents/DefaultPriceComponentByTypeId(${priceComponentTypeId})`,
    {
      params
    }
  ).then(response => {
    return response.data
  })
}

export const apiSearchPriceComponentTypeInstances = async (request: {
  priceComponentTypeId: number
  commodity: number
  companyId: number
  referenceType: number
  searchText?: string
  leadIds?: number[]
  tollIds?: number[]
  presionTensionIds?: number[]
  regionImpuestosIds?: number[]
}): Promise<PriceComponentModelV2[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/PriceComponents/SearchPriceComponentTypeInstances`,
    request
  ).then(response => {
    return response.data
  })
}

// #endregion  -------------------- PRICE COMPONENT TYPES --------------------

export const fetchParameterBasePriceComponents = async (
  commodity: EnumCommodity
): Promise<ArgentiaPricingTableModel[]> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  const activeCompany = getActiveCompany()
  // const apiSuffixUrl = 'https://localhost:59923'

  return await AxiosInstance.get(
    `${apiSuffixUrl}/api/PriceComponents/ParametersBasePriceComponentTypes?commodity=${commodity}&companyID=${activeCompany!.id}`
  ).then(response => {
    return response.data
  })
}

export const apiCreatePriceComponent = async (
  request: CreatePriceComponentRequest
): Promise<PriceComponentAPI> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:65182'

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/PriceComponents/CreatePriceComponent`,
    request
  ).then(response => response.data)
}

export const apiCreatePriceComponentV2 = async (
  request: PriceComponentV2RequestModel
): Promise<PriceComponentAPI> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:59923'

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/PriceComponents/CreatePriceComponent?api-version=2.0`,
    request
  ).then(response => response.data)
}

export const apiUpdatePriceComponentValue = async (
  request: UpdatePriceComponentValueRequest
): Promise<null> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)

  return await AxiosInstance.post(
    `${apiSuffixUrl}/api/PriceComponents/UpdateValueForPriceComponent`,
    request
  ).then(response => response.data)
}

export const apiDeletePriceComponent = async (id: number): Promise<null> => {
  const apiSuffixUrl = getAppConfigurationValue(AppConfigurationKey.PrefixEnviromentArgentia)
  // const apiSuffixUrl = 'https://localhost:65182'

  return await AxiosInstance.delete(
    `${apiSuffixUrl}/api/PriceComponents/DeletePriceComponentWithPrice/${id}`
  ).then(response => response.data)
}
